import React, { useState, useEffect, useCallback } from "react";
import { Timer, Play, Square, Plus, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useApp, formatTimerDisplay } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatRelativeTime } from "../../lib/utils";
import { toast } from "sonner";

export default function TimeTracker({ ticketId, ticketNumber, currentUser, isAgentOrAdmin }) {
    const { timerState, startTimer, stopTimer } = useApp();
    const { getUserById } = useApp();

    const [timeEntries, setTimeEntries] = useState([]);
    const [timeEntriesOpen, setTimeEntriesOpen] = useState(false);
    const [manualTimeInput, setManualTimeInput] = useState("");
    const [manualTimeNote, setManualTimeNote] = useState("");
    const [submittingTime, setSubmittingTime] = useState(false);

    const loadTimeEntries = useCallback(async () => {
        try {
            const entries = await api.getTimeEntries(ticketId);
            setTimeEntries(entries);
        } catch (err) {
            console.error("Failed to load time entries:", err);
        }
    }, [ticketId]);

    useEffect(() => {
        if (ticketId) loadTimeEntries();
    }, [ticketId, loadTimeEntries]);

    const parseTimeToMinutes = (input) => {
        const s = input.trim().toLowerCase();
        if (!s) return null;
        const hm = s.match(/^(\d+(?:\.\d+)?)\s*h\s*(?:(\d+)\s*m?)?$/);
        if (hm) {
            const hours = parseFloat(hm[1]);
            const mins = hm[2] ? parseInt(hm[2], 10) : 0;
            return Math.round(hours * 60 + mins);
        }
        const mOnly = s.match(/^(\d+)\s*m$/);
        if (mOnly) return parseInt(mOnly[1], 10);
        const num = parseFloat(s);
        if (!isNaN(num) && num > 0) return Math.round(num);
        return null;
    };

    const handleAddManualTime = async () => {
        const minutes = parseTimeToMinutes(manualTimeInput);
        if (!minutes || minutes <= 0) {
            toast.error("Format invalide. Exemples : 90, 1h30, 1.5h");
            return;
        }
        try {
            setSubmittingTime(true);
            await api.createTimeEntry(ticketId, currentUser.id, {
                duration_minutes: minutes,
                note: manualTimeNote.trim() || null,
            });
            setManualTimeInput("");
            setManualTimeNote("");
            toast.success(`${minutes} min ajoutées`);
            loadTimeEntries();
        } catch {
            toast.error("Erreur lors de l'ajout du temps");
        } finally {
            setSubmittingTime(false);
        }
    };

    const handleDeleteTimeEntry = async (entryId) => {
        if (!window.confirm("Supprimer cette entrée de temps ?")) return;
        try {
            await api.deleteTimeEntry(entryId);
            toast.success("Entrée supprimée");
            loadTimeEntries();
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleTimerToggle = async () => {
        if (timerState.isRunning && timerState.ticketId === ticketId) {
            await stopTimer();
            loadTimeEntries();
        } else {
            startTimer(ticketId, ticketNumber);
        }
    };

    const totalTimeMinutes = timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);

    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes} min`;
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : `${h}h`;
    };

    if (!isAgentOrAdmin) return null;

    return (
        <div className="card" data-testid="time-tracking-card">
            <div className="card-header">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Timer className="w-4 h-4 text-gray-500" />
                        Temps passé
                    </h3>
                    {totalTimeMinutes > 0 && (
                        <span className="text-xs font-mono font-semibold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full" data-testid="total-time">
                            {formatDuration(totalTimeMinutes)}
                        </span>
                    )}
                </div>
            </div>
            <div className="card-body space-y-3">
                {timerState.isRunning && timerState.ticketId === ticketId ? (
                    <button
                        onClick={handleTimerToggle}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
                        data-testid="stop-timer-btn"
                    >
                        <Square className="w-3.5 h-3.5 fill-red-600" />
                        <span>Arrêter</span>
                        <span className="font-mono ml-1">{formatTimerDisplay(timerState.elapsedSeconds)}</span>
                    </button>
                ) : (
                    <button
                        onClick={handleTimerToggle}
                        disabled={timerState.isRunning}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="start-timer-btn"
                        title={timerState.isRunning ? `Chrono actif sur ${timerState.ticketNumber}` : undefined}
                    >
                        <Play className="w-3.5 h-3.5 fill-emerald-600" />
                        {timerState.isRunning
                            ? `Chrono actif sur ${timerState.ticketNumber}`
                            : "Démarrer le chrono"}
                    </button>
                )}

                <div className="border-t pt-3 space-y-2">
                    <p className="text-xs font-medium text-gray-500">Ajouter du temps</p>
                    <div className="flex gap-2">
                        <Input
                            value={manualTimeInput}
                            onChange={(e) => setManualTimeInput(e.target.value)}
                            placeholder="Ex: 90, 1h30, 1.5h"
                            className="flex-1 text-sm h-8"
                            data-testid="manual-time-input"
                        />
                        <Button
                            size="sm"
                            onClick={handleAddManualTime}
                            disabled={!manualTimeInput.trim() || submittingTime}
                            className="h-8 px-3"
                            data-testid="add-time-btn"
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    <Input
                        value={manualTimeNote}
                        onChange={(e) => setManualTimeNote(e.target.value)}
                        placeholder="Note (optionnel)"
                        className="text-sm h-8"
                        data-testid="manual-time-note"
                    />
                </div>

                {timeEntries.length > 0 && (
                    <div className="border-t pt-3">
                        <button
                            onClick={() => setTimeEntriesOpen(!timeEntriesOpen)}
                            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 w-full"
                            data-testid="time-entries-toggle"
                        >
                            {timeEntriesOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            Historique ({timeEntries.length} entrée{timeEntries.length > 1 ? "s" : ""})
                        </button>
                        {timeEntriesOpen && (
                            <div className="mt-2 space-y-2 max-h-60 overflow-y-auto" data-testid="time-entries-list">
                                {timeEntries.map((entry) => {
                                    const agent = getUserById(entry.agent_id);
                                    return (
                                        <div key={entry.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-md group" data-testid={`time-entry-${entry.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-semibold text-gray-900">{formatDuration(entry.duration_minutes)}</span>
                                                    <span className="text-[10px] text-gray-400">{agent ? `${agent.first_name} ${agent.last_name}` : "—"}</span>
                                                </div>
                                                {entry.note && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{entry.note}</p>}
                                                <p className="text-[10px] text-gray-400 mt-0.5">{formatRelativeTime(entry.created_at)}</p>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteTimeEntry(entry.id)}
                                                className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                                title="Supprimer"
                                                data-testid={`delete-time-${entry.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
